import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { SvgIcon } from '@progress/kendo-react-common';
import { Page } from '@progress/kendo-react-dropdowns';
import {
    Grid,
    GridColumn,
    GridColumnProps,
    GridColumnReorderEvent,
    GridContextMenuEvent,
    GridCustomCellProps,
    GridCustomHeaderCellProps,
    GridFilterChangeEvent,
    GridHandle,
    GridPageChangeEvent,
    GridSortChangeEvent
} from '@progress/kendo-react-grid';
import { Skeleton } from '@progress/kendo-react-indicators';
import * as React from 'react';
import AirportRecord from '../../../airports/airport';
import { AirportSelection } from '../../../common/enums';
import { ListState } from '../../../components/common/types';
import { FilterOutline } from '../../../icons/icons';
import { queryAirportGrid } from '../../../services/grid-query-service';
import { ColumnMenu } from '../../components/grid/ColumnMenu';
import { ColumnMenuContext } from '../../components/grid/ColumnMenuContext';
import { hasFilter } from '../../components/grid/helpers';
import { EventBus } from '../../contexts/event-bus';
import { useAppDispatch, useAppSelector } from '../../hooks/store-hooks';
import { useSafeEffect } from '../../hooks/use-safe-effect';
import { setSetting } from '../../state/slices/settings-slice';
import { store } from '../../state/store';
import { getFormattedAirportField } from './AirportListCells';

function createInitialDataStateAndOrder(all: GridAirportColumnsWithExtra[]) {
    const settings = store.getState().settings;

    const savedState = settings.airportListState;
    const savedOrder = settings.airportListOrder;
    return {
        // TODO: take out columns that are not part of the order
        filter: savedState?.filter,
        sort: savedState?.sort,
        columnState: savedOrder
            ? (savedOrder.map((saved) => all.find((x) => x.id === saved)).filter(Boolean) as GridAirportColumnsWithExtra[])
            : all.filter((x) => x.default)
    };
}

const AirportList = () => {
    const allColumns = _allColumns;
    const initialState = React.useMemo(() => createInitialDataStateAndOrder(allColumns), []);
    const lastUpdate = useAppSelector((x) => x.global.flightWorker.lastUpdate?.time);
    const [page, setPage] = React.useState<Page>({ skip: 0, take: 80 });
    const [filter, setFilter] = React.useState(initialState.filter);
    const [sort, setSort] = React.useState(initialState.sort);
    const storedGridFilterAndSort = useAppSelector((x) => x.settings.airportListState);
    const [data, setData] = React.useState({ skip: 0, data: [] as any });
    const [total, setTotal] = React.useState(0);
    const [columnState, setColumnState] = React.useState<GridColumnProps[]>(initialState.columnState);

    const gridRef = React.useRef<GridHandle>(null);
    const dispatch = useAppDispatch();

    React.useEffect(() => {
        gridRef.current?.fitColumns(initialState.columnState.map((x) => x.id) as string[]);
    }, [initialState]);

    useSafeEffect(() => {
        queryAirportGrid({ skip: page.skip, take: page.take, filter, sort }).then((result) => {
            setTotal(result.total);
            setData({
                skip: page.skip,
                data: result.data
            });
        });
    }, [filter, sort, page, lastUpdate]);

    const handlePageChanged = (e: GridPageChangeEvent) => {
        setPage(e.page);
    };

    const storeFilterAndSort = (state: Partial<ListState>) => {
        dispatch(setSetting('airportListState', { ...storedGridFilterAndSort, ...state }));
    };

    const filterChanged = React.useCallback((event: GridFilterChangeEvent) => {
        setFilter(event.filter);
        storeFilterAndSort({ filter: event.filter });
    }, []);

    const sortChanged = React.useCallback((event: GridSortChangeEvent) => {
        if (event.nativeEvent.shiftKey) {
            if (event.sort.length > 2) {
                event.sort.splice(1, 1);
            }
        } else {
            if (event.sort.length > 1) {
                event.sort.splice(0, event.sort.length - 1);
            }
        }

        setSort(event.sort);
        storeFilterAndSort({ sort: event.sort });
    }, []);

    const saveOrder = React.useCallback((columns: GridColumnProps[]) => {
        const orderIds = columns.map((x) => x.id!);
        dispatch(setSetting('airportListOrder', orderIds));
    }, []);

    const columnOrderChanged = React.useCallback((event: GridColumnReorderEvent) => {
        setColumnState(event.columns);
        saveOrder(event.columns);
    }, []);

    const onColumnsChange = React.useCallback((cols: GridColumnProps[]) => {
        setColumnState(cols);
        saveOrder(cols);
        self.setTimeout(() => gridRef.current?.fitColumns(cols.map((x) => x.id) as string[]), 0);
    }, []);

    const handleContextMenu = React.useCallback((event: GridContextMenuEvent) => {
        event.syntheticEvent.preventDefault();
        const item = event.dataItem as AirportRecord;

        EventBus.fireContextMenu([{ kind: 'airport', airportId: item.recordId }], event.nativeEvent);
    }, []);

    const displayData = React.useMemo(() => {
        /*
         * Long story short: grid will internally update its skip state even if you don't pass a new skip prop to it. This is different from what it used to do.
         * Previously we were able to wait with setting the skip prop until we have data and then set both at once. But now, the grid will update skip internally and re-render
         * with the data for previous skip shifted according to the new skip and shortly after that the real data will arrive creating a jigger effect.
         * Instead, we keep track of the data that we have and skip skip it belongs to. Here we calculate what's the delta between previous skip and this skip and we create
         * a data that copies items from the previous data array and places them in the new array according to the skip delta.
         * This will result in Grid not having all the data to display but at least nothing will shift unexpectedly. When the real data arrives those gaps will be filled in.
         */
        const itemCount = Math.min(page.take, Math.max(total - page.skip, 0));
        const arr = new Array(itemCount);
        const skipDiff = page.skip - data.skip;
        for (let i = 0; i < arr.length; i++) {
            arr[i] = data.data[skipDiff + i] ?? { recordId: i, auto: true };
        }
        return arr;
    }, [data, page]);

    const HeaderCell = React.useMemo(() => getHeader(filter), [filter]);

    return (
        <ColumnMenuContext.Provider
            value={{
                columnsState: columnState,
                columns: allColumns,
                onColumnsChange
            }}>
            <Grid
                className="grid-hide-filter-row telerik-cobaltify"
                ref={gridRef}
                style={{ height: '100%', width: '100%' }}
                resizable={true}
                reorderable={true}
                sortable={{ mode: 'multiple', allowUnsort: true }}
                filterable={true}
                navigatable={true}
                scrollable={'virtual'}
                fixedScroll={true}
                dataItemKey={'recordId'}
                rowHeight={30}
                skip={page.skip}
                take={page.take}
                data={displayData}
                total={total}
                columnVirtualization
                onContextMenu={handleContextMenu}
                onFilterChange={filterChanged}
                onSortChange={sortChanged}
                columnMenu={ColumnMenu}
                onPageChange={handlePageChanged}
                onColumnReorder={columnOrderChanged}
                cells={{
                    headerCell: HeaderCell
                }}>
                {columnState.map((c) => (
                    <GridColumn key={c.id} {...c} />
                ))}
            </Grid>
        </ColumnMenuContext.Provider>
    );
};
function createGenericCell(column: GridAirportColumnsWithExtra) {
    const getMainCell = (props: GridCustomCellProps) => {
        const airport = props.dataItem as AirportRecord | undefined;
        if (!airport || 'auto' in airport) {
            return (
                <td {...props.tdProps}>
                    <Skeleton shape="text" />
                </td>
            );
        }
        return <td {...props.tdProps}>{getFormattedAirportField(airport, column.dataSelector)}</td>;
    };

    return (props: GridCustomCellProps) => {
        return (
            <>
                {props.tdProps && getMainCell(props)}
                {props.td2Props && <td {...props.td2Props} />}
            </>
        );
    };
}

function getHeader(filter: CompositeFilterDescriptor | undefined) {
    return function (props: GridCustomHeaderCellProps) {
        return (
            <th {...props.thProps}>
                <div className="d-flex gap-1 align-items-center">
                    {hasFilter(filter, props.field) && <SvgIcon className="icon-b-1" size="small" icon={FilterOutline} />}
                    {props.children}
                </div>
            </th>
        );
    };
}

type GridAirportColumnsWithExtra = GridColumnProps & {
    dataSelector: AirportSelection;
    justify?: 'right' | 'left';
    default?: boolean;
};

const _allColumns: GridAirportColumnsWithExtra[] = [
    {
        id: 'hasNotes',
        field: 'hasNotes',
        title: 'Notes',
        dataSelector: AirportSelection.Notes,
        cells: {
            headerCell: (props) => <th {...props.thProps} style={{ width: '25px' }}></th>
        },
        default: true
    },
    {
        id: 'iataId',
        field: 'iataId',
        title: 'IATA ID',
        dataSelector: AirportSelection.iataId,
        default: true
    },
    {
        id: 'icaoId',
        field: 'icaoId',
        title: 'ICAO ID',
        dataSelector: AirportSelection.icaoId,
        default: true
    },
    {
        id: 'MonStatApt',
        field: 'thresholds.monitorStatus',
        filter: 'numeric',
        title: 'MonStatApt',
        dataSelector: AirportSelection.MonStatApt,
        default: true
    },
    {
        id: 'metarFc',
        field: 'metar.flightCategory',
        title: 'Flt Cat (MET)',
        dataSelector: AirportSelection.metarFc,
        default: true
    },
    {
        id: 'metarVis',
        field: 'metar.visibility',
        title: 'Visibility (MET)',
        filter: 'numeric',
        dataSelector: AirportSelection.metarVis,
        default: true
    },
    {
        id: 'metarCeil',
        field: 'metar.ceiling',
        title: 'Ceiling (MET)',
        filter: 'numeric',
        dataSelector: AirportSelection.metarCeil,
        default: true
    },
    {
        id: 'metarWs',
        field: 'metar.windSpeed',
        title: 'Wind (MET)',
        filter: 'numeric',
        dataSelector: AirportSelection.metarWs,
        default: true
    },
    {
        id: 'metarWg',
        field: 'metar.windGusts',
        title: 'Gusts (MET)',
        filter: 'numeric',
        dataSelector: AirportSelection.metarWg,
        default: true
    },
    {
        id: 'metarTmp',
        field: 'metar.temperature',
        title: 'Temp (MET)',
        filter: 'numeric',
        dataSelector: AirportSelection.metarTmp,
        default: true
    },
    {
        id: 'metarAltimeter',
        field: 'metar.altimeter',
        title: 'Altimeter (MET)',
        filter: 'numeric',
        dataSelector: AirportSelection.metarAltimeter,
        default: true
    },
    {
        id: 'taf',
        sortable: false,
        minResizableWidth: 300,
        width: '300px',
        field: 'taf.reportTime',
        title: 'TAF',
        dataSelector: AirportSelection.taf,
        default: true
    },
    {
        id: 'rvrWc',
        field: 'rvr.worstCaseVisibility',
        title: 'RVR-WC',
        filter: 'numeric',
        dataSelector: AirportSelection.rvrWc,
        default: true
    },
    {
        id: 'fltholdinginto',
        field: 'holding.items.length',
        title: 'Flts Hldng Into',
        filter: 'numeric',
        dataSelector: AirportSelection.fltholdinginto,
        default: true
    },
    {
        id: 'gndStp',
        field: 'gndStp',
        title: 'Gnd stop',
        dataSelector: AirportSelection.gndStp,
        default: true
    },
    {
        id: 'gndDelay',
        field: 'gndDelay',
        title: 'Gnd delay',
        dataSelector: AirportSelection.gndDelay,
        default: true
    },
    {
        id: 'clFaa',
        field: 'clFaa',
        title: 'Closure (FAA)',
        dataSelector: AirportSelection.clFaa,
        default: true
    },
    {
        id: 'depDel',
        field: 'depDel',
        title: 'Dep Delay',
        dataSelector: AirportSelection.depDel,
        default: true
    },
    {
        id: 'arrDel',
        field: 'arrDel',
        title: 'Arr Delay',
        dataSelector: AirportSelection.arrDel,
        default: true
    },
    {
        id: 'ArrRate',
        field: 'config.arrivalRate',
        title: 'ArrRate',
        filter: 'numeric',
        dataSelector: AirportSelection.ArrRate
    },
    {
        id: 'ArrRWays',
        field: 'config.arrivalRunway',
        title: 'ArrRWays',
        filter: 'numeric',
        dataSelector: AirportSelection.ArrRWays
    },
    {
        id: 'closureAltPS',
        field: 'diversion.alternateCountPS',
        filter: 'numeric',
        title: 'Alt (P/S)',
        dataSelector: AirportSelection.closureAltPS
    },
    {
        id: 'closureAltAct',
        field: 'diversion.alternateCountActive',
        filter: 'numeric',
        title: 'Alt (act)',
        dataSelector: AirportSelection.closureAltAct
    },
    {
        id: 'closuredm',
        field: 'diversion.closure',
        filter: 'numeric',
        title: 'Closure (DM)',
        dataSelector: AirportSelection.closuredm
    },
    {
        id: 'closurestart',
        field: 'diversion.closeStart',
        filter: 'numeric',
        title: 'Closure Start (DM)',
        dataSelector: AirportSelection.closurestart
    },
    {
        id: 'closureend',
        field: 'diversion.closeEnd',
        filter: 'numeric',
        title: 'Closure End (DM)',
        dataSelector: AirportSelection.closureend
    },
    {
        id: 'closureDestPS',
        field: 'diversion.destinationCountPS',
        filter: 'numeric',
        title: 'Dest (P/S)',
        dataSelector: AirportSelection.closureDestPS
    },
    {
        id: 'closureDestAct',
        field: 'diversion.destinationCountActive',
        filter: 'numeric',
        title: 'Dest (act)',
        dataSelector: AirportSelection.closureDestAct
    },
    {
        id: 'DepRate',
        field: 'config.departureRate',
        title: 'DepRate',
        filter: 'numeric',
        dataSelector: AirportSelection.DepRate
    },
    {
        id: 'Deicing',
        field: 'deicing.status',
        title: 'De-icing',
        filter: 'numeric',
        dataSelector: AirportSelection.Deicing
    },
    {
        id: 'DepRWays',
        field: 'config.departureRunway',
        title: 'DepRWays',
        filter: 'text',
        dataSelector: AirportSelection.DepRWays
    },
    {
        id: 'metarWd',
        field: 'metar.windDirection',
        title: 'Wind Dir (MET)',
        filter: 'numeric',
        dataSelector: AirportSelection.metarWd
    },
    {
        id: 'metarDew',
        field: 'metar.dewPoint',
        title: 'Dew Pt (MET)',
        filter: 'numeric',
        dataSelector: AirportSelection.metarDew
    },
    {
        id: 'metartime',
        field: 'metar.reportTime',
        title: 'METAR time',
        filter: 'numeric',
        dataSelector: AirportSelection.metartime
    },
    {
        id: 'taftime',
        field: 'taf.reportTime',
        title: 'TAF time',
        filter: 'numeric',
        dataSelector: AirportSelection.taftime
    },
    {
        id: 'diversioncapacityairlinenarrow',
        field: 'diversion.capacity.airline.narrow',
        filter: 'numeric',
        title: 'Div Cap Air Narr',
        dataSelector: AirportSelection.diversioncapacityairlinenarrow
    },
    {
        id: 'diversioncapacityairlinewide',
        field: 'diversion.capacity.airline.wide',
        filter: 'numeric',
        title: 'Div Cap Air Wide',
        dataSelector: AirportSelection.diversioncapacityairlinewide
    },
    {
        id: 'diversioncapacitynetworknarrow',
        field: 'diversion.capacity.network.narrow',
        filter: 'numeric',
        title: 'Div Cap Net Narr',
        dataSelector: AirportSelection.diversioncapacitynetworknarrow
    },
    {
        id: 'diversioncapacitynetworkwide',
        field: 'diversion.capacity.network.wide',
        filter: 'numeric',
        title: 'Div Cap Net Wide',
        dataSelector: AirportSelection.diversioncapacitynetworkwide
    },
    {
        id: 'diversioncapacityothernarrow',
        field: 'diversion.capacity.other.narrow',
        filter: 'numeric',
        title: 'Div Cap Othr Narr',
        dataSelector: AirportSelection.diversioncapacityothernarrow
    },
    {
        id: 'diversioncapacityotherwide',
        field: 'diversion.capacity.other.wide',
        filter: 'numeric',
        title: 'Div Cap Othr Wide',
        dataSelector: AirportSelection.diversioncapacityotherwide
    },
    {
        id: 'diversiondivertairlinenarrow',
        field: 'diversion.divert.airline.narrow',
        filter: 'numeric',
        title: 'Div Act Air Narr',
        dataSelector: AirportSelection.diversiondivertairlinenarrow
    }, //TO Check nameing between divert (Web) and active (FE Pro)
    {
        id: 'diversiondivertairlinewide',
        field: 'diversion.divert.airline.wide',
        filter: 'numeric',
        title: 'Div Act Air Wide',
        dataSelector: AirportSelection.diversiondivertairlinewide
    }, //TO Check nameing between divert and active
    {
        id: 'diversiondivertnetworknarrow',
        field: 'diversion.divert.network.narrow',
        filter: 'numeric',
        title: 'Div Act Net Narr',
        dataSelector: AirportSelection.diversiondivertnetworknarrow
    },
    {
        id: 'diversiondivertnetworkwide',
        field: 'diversion.divert.network.wide',
        filter: 'numeric',
        title: 'Div Act Net Wide',
        dataSelector: AirportSelection.diversiondivertnetworkwide
    },
    {
        id: 'diversiondivertothernarrow',
        field: 'diversion.divert.other.narrow',
        filter: 'numeric',
        title: 'Div Act Othr Narr',
        dataSelector: AirportSelection.diversiondivertothernarrow
    },
    {
        id: 'diversiondivertotherwide',
        field: 'diversion.divert.other.wide',
        filter: 'numeric',
        title: 'Div Act Othr Wide',
        dataSelector: AirportSelection.diversiondivertotherwide
    },
    {
        id: 'diversionavailableairlinenarrow',
        field: 'diversion.available.airline.narrow',
        filter: 'numeric',
        title: 'Div Avl Air Narr',
        dataSelector: AirportSelection.diversionavailableairlinenarrow
    },
    {
        id: 'diversionavailableairlinewide',
        field: 'diversion.available.airline.wide',
        filter: 'numeric',
        title: 'Div Avl Air Wide',
        dataSelector: AirportSelection.diversionavailableairlinewide
    },
    {
        id: 'diversionavailablenetworknarrow',
        field: 'diversion.available.network.narrow',
        filter: 'numeric',
        title: 'Div Avl Net Narr',
        dataSelector: AirportSelection.diversionavailablenetworknarrow
    },
    {
        id: 'diversionavailablenetworkwide',
        field: 'diversion.available.network.wide',
        filter: 'numeric',
        title: 'Div Avl Net Wide',
        dataSelector: AirportSelection.diversionavailablenetworkwide
    },
    {
        id: 'diversionavailableothernarrow',
        field: 'diversion.available.other.narrow',
        filter: 'numeric',
        title: 'Div Avl Othr Narr',
        dataSelector: AirportSelection.diversionavailableothernarrow
    },
    {
        id: 'diversionavailableotherwide',
        field: 'diversion.available.other.wide',
        filter: 'numeric',
        title: 'Div Avl Othr Wide',
        dataSelector: AirportSelection.diversionavailableotherwide
    },
    {
        id: 'diversioncomments',
        field: 'diversion.comments',
        filter: 'text',
        title: 'Div Man Comments',
        dataSelector: AirportSelection.diversioncomments
    },
    {
        id: 'diversioncapacityairlinetotal',
        field: 'diversion.capacity.airline.total',
        filter: 'numeric',
        title: 'Div Cap Air Tot',
        dataSelector: AirportSelection.diversioncapacityairlinetotal
    },
    {
        id: 'diversioncapacitynetworktotal',
        field: 'diversion.capacity.network.total',
        filter: 'numeric',
        title: 'Div Cap Net Tot',
        dataSelector: AirportSelection.diversioncapacitynetworktotal
    },
    {
        id: 'diversioncapacityothertotal',
        field: 'diversion.capacity.other.total',
        filter: 'numeric',
        title: 'Div Cap Othr Tot',
        dataSelector: AirportSelection.diversioncapacityothertotal
    },
    {
        id: 'diversiondivertairlinetotal',
        field: 'diversion.divert.airline.total',
        filter: 'numeric',
        title: 'Div Act Air Tot',
        dataSelector: AirportSelection.diversiondivertairlinetotal
    }, //TO Check nameing between divert (Web) and active (FE Pro)
    {
        id: 'diversiondivertnetworktotal',
        field: 'diversion.divert.network.total',
        filter: 'numeric',
        title: 'Div Act Net Tot',
        dataSelector: AirportSelection.diversiondivertnetworktotal
    }, //TO Check nameing between divert and active
    {
        id: 'diversiondivertothertotal',
        field: 'diversion.divert.other.total',
        filter: 'numeric',
        title: 'Div Act Othr Tot',
        dataSelector: AirportSelection.diversiondivertothertotal
    },
    {
        id: 'diversionavailableairlinetotal',
        field: 'diversion.available.airline.total',
        filter: 'numeric',
        title: 'Div Avl Air Tot',
        dataSelector: AirportSelection.diversionavailableairlinetotal
    },
    {
        id: 'diversionavailablenetworktotal',
        field: 'diversion.available.network.total',
        filter: 'numeric',
        title: 'Div Avl Net Tot',
        dataSelector: AirportSelection.diversionavailablenetworktotal
    },
    {
        id: 'diversionavailableothertotal',
        field: 'diversion.available.other.total',
        filter: 'numeric',
        title: 'Div Avl Othr Tot',
        dataSelector: AirportSelection.diversionavailableothertotal
    },
    {
        id: 'MonStatOrg',
        field: 'thresholds.origin.monitorStatus',
        filter: 'numeric',
        title: 'MonStatOrg',
        dataSelector: AirportSelection.MonStatOrg
    },
    {
        id: 'MonStatDst',
        field: 'thresholds.destination.monitorStatus',
        filter: 'numeric',
        title: 'MonStatDst',
        dataSelector: AirportSelection.MonStatDst
    },
    {
        id: 'MonStatAlt',
        field: 'thresholds.alternate.monitorStatus',
        filter: 'numeric',
        title: 'MonStatAlt',
        dataSelector: AirportSelection.MonStatAlt
    }
];

_allColumns.forEach((x) => (x.cells = { data: createGenericCell(x) }));

export default AirportList;
